import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { CompetitionProps } from './Competition.types';
import { useCompetition } from './useCompetition';
import { CompetitionForm } from './CompetitionForm/CompetitionForm';
import { LoginModal } from '../LoginModal';
import { useMembershipStatus } from '../../hooks/useMembershipStatus';
import { checkUserAccess } from '../shared/userAccessibility.utils';
import * as S from './Competition.styles';
import CompetitionResults from './CompetitionResult/CompetitionResults';

export const Competition = ({
  competitionId,
  title,
  ingress,
  questions,
  endDate,
  image,
  video,
  style,
  answerStatus,
  requireTermsApproval,
  termsLinkSlug,
  userAccessibility,
}: CompetitionProps) => {
  const [showLoginModal, setLoginShowModal] = useState(false);
  const { isAuthenticated } = useAuth0();
  const { isMembershipActive } = useMembershipStatus();

  const membershipRole = userAccessibility?.membershipRole;

  const hasAccess = checkUserAccess({
    userAccessibility,
    isAuthenticated,
    isMembershipActive,
  });

  const {
    isCorrect,
    isFreshVote,
    isSubmitting,
    selectedAnswers,
    termsAccepted,
    isFormValid,
    handleAnswerSelect,
    handleTermsToggle,
    handleSubmit,
  } = useCompetition({
    competitionId,
    questions,
    requireTermsApproval,
    answerStatus,
  });

  const handleAnswerSelectWithAccessCheck = (questionId: string, answerId: string) => {
    if (!hasAccess) {
      setLoginShowModal(true);
      return;
    }
    handleAnswerSelect(questionId, answerId);
  };

  const handleCloseModal = () => setLoginShowModal(false);

  const isForMembersOnly = membershipRole?.toLowerCase() === 'member';
  const loginModalCopy = {
    loginRequired: {
      title: 'Du må være logget inn for å delta',
      body: 'Har du ikke bruker fra før, kan du enkelt opprette en med Vipps, SMS eller e-post. Konkurransen krever ikke medlemskap.',
    },
    membersOnly: {
      title: 'Du må være medlem for å delta i konkurransen',
      body: 'Melder du deg inn, får du også tilgang til alle medlemsfordelene våre.',
    },
  };

  return (
    <>
      <S.Wrapper $style={style}>
        <S.Container $style={style}>
          <S.Content>
            {isFreshVote ? (
              <CompetitionResults isCorrect={isCorrect} style={style} termsLinkSlug={termsLinkSlug} />
            ) : (
              <CompetitionForm
                title={title}
                ingress={ingress}
                questions={questions}
                endDate={endDate}
                image={image}
                video={video}
                style={style}
                hasAnswered={answerStatus.hasAnswered ?? false}
                requireTermsApproval={requireTermsApproval}
                selectedAnswers={selectedAnswers}
                termsAccepted={termsAccepted}
                isSubmitting={isSubmitting}
                isFormValid={isFormValid}
                termsLinkSlug={termsLinkSlug}
                onAnswerSelect={handleAnswerSelectWithAccessCheck}
                onTermsToggle={handleTermsToggle}
                onSubmit={handleSubmit}
              />
            )}
          </S.Content>
        </S.Container>
      </S.Wrapper>
      <LoginModal
        isOpen={showLoginModal}
        onClose={handleCloseModal}
        title={isForMembersOnly ? loginModalCopy.membersOnly.title : loginModalCopy.loginRequired.title}
        body={isForMembersOnly ? loginModalCopy.membersOnly.body : loginModalCopy.loginRequired.body}
        showBecomeMemberButton={isForMembersOnly}
      />
    </>
  );
};

export default Competition;
